<script setup>
import { computed } from 'vue';

// Live preview of the branding being edited (not yet saved): sidebar brand
// tile, active menu item, primary button and the sign-in tagline.
const props = defineProps({
  name: { type: String, default: '' },
  shortName: { type: String, default: '' },
  tagline: { type: String, default: '' },
  color: { type: String, default: '#E35E93' },
  logoUrl: { type: String, default: '' },
});

const style = computed(() => {
  const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(props.color || '');
  const [r, g, b] = m ? m.slice(1).map((h) => parseInt(h, 16)) : [227, 94, 147];
  // Readable text on the brand colour (same idea as Vuetify's on-primary).
  const light = (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.6;
  return {
    '--p-brand': `rgb(${r}, ${g}, ${b})`,
    '--p-soft': `rgba(${r}, ${g}, ${b}, 0.1)`,
    '--p-on': light ? '#0a0a0a' : '#ffffff',
  };
});
</script>

<template>
  <div class="preview" :style="style" aria-label="Branding preview">
    <div class="preview__label">Preview</div>
    <div class="preview__body">
      <div class="preview__sidebar">
        <div class="preview__brand">
          <img v-if="logoUrl" :src="logoUrl" alt="" class="preview__logo" />
          <div class="preview__brand-text">
            <span class="preview__short">{{ shortName || 'Short name' }}</span>
            <span class="preview__sub">Class Management</span>
          </div>
        </div>
        <div class="preview__nav preview__nav--active">
          <v-icon size="16">mdi-view-dashboard-outline</v-icon> Dashboard
        </div>
        <div class="preview__nav">
          <v-icon size="16">mdi-account-multiple-outline</v-icon> Accounts
        </div>
      </div>

      <div class="preview__main">
        <div class="preview__school">{{ name || 'School name' }}</div>
        <p v-if="tagline" class="preview__tagline">“{{ tagline }}”</p>
        <div class="preview__actions">
          <span class="preview__btn">Primary action</span>
          <span class="preview__field">Focused field</span>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.preview {
  border: 1px solid var(--border);
  border-radius: 12px;
  overflow: hidden;
}
.preview__label {
  padding: 8px 12px;
  border-bottom: 1px solid var(--border);
  background: var(--subtle);
  font-size: 12px;
  font-weight: 500;
  color: var(--muted-foreground);
}
.preview__body {
  display: grid;
  grid-template-columns: 180px 1fr;
}
.preview__sidebar {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 10px;
  background: var(--subtle);
  border-right: 1px solid var(--border);
}
.preview__brand {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 4px 10px;
}
.preview__logo {
  width: 28px;
  height: 28px;
  object-fit: contain;
  border-radius: 6px;
}
.preview__brand-text {
  display: grid;
  min-width: 0;
  line-height: 1.2;
}
.preview__short {
  font-size: 13px;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.preview__sub {
  font-size: 11px;
  color: var(--muted-foreground);
}
.preview__nav {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 30px;
  padding: 0 8px;
  border-radius: 6px;
  font-size: 13px;
  color: var(--foreground);
}
.preview__nav--active {
  background: var(--p-soft);
  color: var(--p-brand);
  font-weight: 500;
}
.preview__main {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 8px;
  padding: 16px;
  min-width: 0;
}
.preview__school {
  font-size: 15px;
  font-weight: 600;
}
.preview__tagline {
  margin: 0;
  font-size: 13px;
  font-style: italic;
  color: var(--muted-foreground);
}
.preview__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 4px;
}
.preview__btn {
  display: inline-flex;
  align-items: center;
  height: 32px;
  padding: 0 12px;
  border-radius: 8px;
  background: var(--p-brand);
  color: var(--p-on);
  font-size: 13px;
  font-weight: 500;
}
.preview__field {
  display: inline-flex;
  align-items: center;
  height: 32px;
  padding: 0 12px;
  border-radius: 8px;
  background: var(--card);
  box-shadow: inset 0 0 0 1px var(--p-brand), 0 0 0 3px var(--p-soft);
  font-size: 13px;
  color: var(--muted-foreground);
}
@media (max-width: 600px) {
  .preview__body {
    grid-template-columns: 1fr;
  }
  .preview__sidebar {
    border-right: 0;
    border-bottom: 1px solid var(--border);
  }
}
</style>
